import React, { useEffect, useState } from 'react'
import { buildUrl } from '../../services/urlService'
import { useBlogNavigation } from '../../hooks/navigation/useBlogNavigation'
import { useCategoriesI18n } from '../../hooks/i18n/useCategoriesI18n'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import StandardButton, { StandardWhiteButton, StandardButtonArrowIcon } from './standardButton'
import { getI18nText } from '../../services/i18nService'
import { Link } from 'gatsby'
import CTA from './cta'
import { setFontWeightClassNames } from '../../services/forceFontWeightService'

const PageRow = ({ locale, row, index, videoControlObject, id, featuredBlogs, suppressTopMargin }) => {
  const categoryMicrocopy = useCategoriesI18n(locale)
  const blogBaseUrl = buildUrl(useBlogNavigation(locale), '')
  const [sectionClasses, setSectionClasses] = useState('grid-lister is-innovations')
  const [titleClassNames, setTitleClassNames] = useState('force-font__light')
  const [blogTitleClassNames, setBlogTitleClassNames] = useState('force-font__light')

  useEffect(() => {
    let mySectionClasses = 'grid-lister is-innovations'
    if (row.textColour === '#FFFFFF') {
      mySectionClasses = mySectionClasses + ' text-light'
    } else if (row.textColour === '#000000') {
      mySectionClasses = mySectionClasses + ' text-dark'
    }

    if (row.internal.type && row.internal.type === 'ContentfulBlogRow') {
      if (row.blogs) {
        mySectionClasses = mySectionClasses + ` grid-col-${row.blogs.length}`
      } else {
        mySectionClasses = mySectionClasses + ' grid-col-4'
      }
    } else {
      mySectionClasses = mySectionClasses + ` grid-col-${row.numberOfItems}`
    }

    setFontWeightClassNames(row.titleFontWeight, setTitleClassNames)
    setFontWeightClassNames(row.blogTitleFontWeight, setBlogTitleClassNames)

    setSectionClasses(mySectionClasses)
  }, [row])

  const className = suppressTopMargin ? `${sectionClasses} suppress-top-margin` : sectionClasses

  return (
    <section
      key={`row-${index}`}
      className={className}
      style={{ backgroundColor: row.colour }}
      id={id}
    >
      <div className="container">
        <div className="row">
          <div className="col">
            <h2 className={`text-center ${titleClassNames}`}>{row.title}</h2>
          </div>
        </div>
        <div className="row gy-5">
          {row.pageBlocks !== undefined && row.pageBlocks !== null
            ? row.pageBlocks.map((pageBlock, pageBlockIndex) => {
                let pageBlockImage
                if (pageBlock.image !== null && pageBlock.image !== undefined) {
                  pageBlockImage = getImage(pageBlock.image)
                }
                switch (pageBlock.type) {
                  case 'video':
                    return (
                      <VideoPageBlock
                        videoControlObject={videoControlObject}
                        pageBlock={pageBlock}
                      ></VideoPageBlock>
                    )

                  default:
                    return (
                      <PageBlock
                        key={`${pageBlock.id}-${pageBlockIndex}`}
                        pageBlock={pageBlock}
                        pageBlockImage={pageBlockImage}
                        categoryMicrocopy={categoryMicrocopy}
                        backgroundColor={row.colour}
                      />
                    )
                }
              })
            : null}
          {row.internal.type === 'ContentfulBlogRow'
            ? row.blogs !== null && row.blogs !== undefined
              ? row.blogs.map((blog, blogIndex) => {
                  let blogImage
                  if (blog.image !== null && blog.image !== undefined) {
                    blogImage = getImage(blog.image)
                  }

                  return (
                    <BlogWithImage
                      key={`${blog.id}-${blogIndex}`}
                      blog={blog}
                      blogImage={blogImage}
                      baseUrl={blogBaseUrl}
                      titleClassNames={blogTitleClassNames}
                    />
                  )
                })
              : featuredBlogs.map((blog, blogIndex) => {
                  let blogImage
                  if (blog.image !== null && blog.image !== undefined) {
                    blogImage = getImage(blog.image)
                  }

                  return (
                    <BlogWithImage
                      key={`${blog.id}-${blogIndex}`}
                      blog={blog}
                      blogImage={blogImage}
                      baseUrl={blogBaseUrl}
                      titleClassNames={blogTitleClassNames}
                    />
                  )
                })
            : null}
          {row.internal.type === 'ContentfulCta' ? <CTA ctaObject={row} /> : null}
        </div>
      </div>
    </section>
  )
}

export default PageRow

export const PageBlock = ({ pageBlock, pageBlockImage, categoryMicrocopy, backgroundColor }) => {
  const [buttonColourType, setButtonColourType] = useState('standard')

  useEffect(() => {
    let myButtonColourType = 'standard'

    if (backgroundColor) {
      switch (backgroundColor) {
        case '#441f54':
        case '#404142':
          myButtonColourType = 'white'
          break
        default:
          myButtonColourType = 'standard'
      }
    }

    setButtonColourType(myButtonColourType)
  }, [backgroundColor])

  return (
    <>
      {pageBlockImage !== undefined ? (
        <PageBlockWithImage
          pageBlock={pageBlock}
          pageBlockImage={pageBlockImage}
          categoryMicrocopy={categoryMicrocopy}
          buttonType={buttonColourType}
        />
      ) : (
        <PageBlockWithoutImage pageBlock={pageBlock} categoryMicrocopy={categoryMicrocopy} />
      )}
    </>
  )
}

export const VideoPageBlock = ({ videoControlObject, pageBlock }) => {
  const [titleClassNames, setTitleClassNames] = useState('force-font__bold')
  let image = getImage(pageBlock.page.thumbnail)

  useEffect(() => {
    setFontWeightClassNames(pageBlock.titleFontWeight, setTitleClassNames)
  }, [pageBlock])

  function onVideoClick() {
    videoControlObject.setVideoUrl(pageBlock.page.vimeoLink)
    videoControlObject.setIsVideoModalOpen(true)
  }

  return (
    <div className="col grid-lister__item d-flex flex-column">
      <div className="grid-lister__details d-flex flex-column flex-grow-1 p-0">
        <div className="grid-lister__image article__media is-video">
          <div onClick={onVideoClick} className="article__image m-0">
            <GatsbyImage
              image={image}
              className="image__fit--cover override-gatsby homepageImg"
              imgClassName="image__fit--cover no-opacity"
            ></GatsbyImage>
          </div>
        </div>
        <div className="grid-lister__text">
          <h4 className={titleClassNames}>{pageBlock.title}</h4>
          <>
            {pageBlock.description ? (
              <p className="my-4">{pageBlock.description.description}</p>
            ) : null}
          </>
        </div>
      </div>
    </div>
  )
}

export const PageBlockWithImage = ({
  pageBlock,
  pageBlockImage,
  categoryMicrocopy,
  buttonType,
}) => {
  const [titleClassNames, setTitleClassNames] = useState('force-font__light')

  useEffect(() => {
    setFontWeightClassNames(pageBlock.titleFontWeight, setTitleClassNames)
  }, [pageBlock])

  return (
    <div className="col grid-lister__item d-flex flex-column">
      {pageBlock.url ? (
        <>
          <div className="grid-lister__image">
            <div className="ratio ratio-16x9 homepage-image-height-fix">
              <a href={pageBlock.url} target="_blank">
                <GatsbyImage
                  className="image__fit--cover override-gatsby homepageImg"
                  imgClassName="image__fit--cover"
                  image={pageBlockImage}
                  alt=""
                ></GatsbyImage>
              </a>
            </div>
          </div>
          <div className="grid-lister__text d-flex flex-column align-items-start">
            <h4 className={titleClassNames}>
              <a href={pageBlock.url} target="_blank">
                {pageBlock.title}
              </a>
            </h4>
            <>
              {pageBlock.description ? (
                <p className="mt-3">{pageBlock.description.description}</p>
              ) : null}
            </>
            <>
              {buttonType === 'white' ? (
                <StandardWhiteButton
                  path={pageBlock.url}
                  external={true}
                  icon={StandardButtonArrowIcon}
                >
                  {pageBlock.buttonText !== null && pageBlock.buttonText !== ''
                    ? pageBlock.buttonText
                    : getI18nText(categoryMicrocopy, 'categories.discover')}
                </StandardWhiteButton>
              ) : (
                <StandardButton path={pageBlock.url} external={true} icon={StandardButtonArrowIcon}>
                  {pageBlock.buttonText !== null && pageBlock.buttonText !== ''
                    ? pageBlock.buttonText
                    : getI18nText(categoryMicrocopy, 'categories.discover')}
                </StandardButton>
              )}
            </>
          </div>
        </>
      ) : (
        <>
          <div className="grid-lister__image">
            <div className="ratio ratio-16x9 homepage-image-height-fix">
              <Link to={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}>
                <GatsbyImage
                  className="image__fit--cover override-gatsby homepageImg"
                  imgClassName="image__fit--cover"
                  image={pageBlockImage}
                  alt=""
                ></GatsbyImage>
              </Link>
            </div>
          </div>
          <div className="grid-lister__text d-flex flex-column align-items-start">
            <h4 className={titleClassNames}>
              <Link to={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}>
                {pageBlock.title}
              </Link>
            </h4>
            <>
              {pageBlock.description ? (
                <p className="mt-3">{pageBlock.description.description}</p>
              ) : null}
            </>
            <>
              {buttonType === 'white' ? (
                <StandardWhiteButton
                  path={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}
                  icon={StandardButtonArrowIcon}
                >
                  {pageBlock.buttonText !== null && pageBlock.buttonText !== ''
                    ? pageBlock.buttonText
                    : getI18nText(categoryMicrocopy, 'categories.discover')}
                </StandardWhiteButton>
              ) : (
                <StandardButton
                  path={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}
                  icon={StandardButtonArrowIcon}
                >
                  {pageBlock.buttonText !== null && pageBlock.buttonText !== ''
                    ? pageBlock.buttonText
                    : getI18nText(categoryMicrocopy, 'categories.discover')}
                </StandardButton>
              )}
            </>
          </div>
        </>
      )}
    </div>
  )
}

export const BlogWithImage = ({ blog, blogImage, baseUrl, titleClassNames }) => {
  const [summary, setSummary] = useState()
  const [toUrl, setToUrl] = useState(baseUrl)

  useEffect(() => {
    if (
      blog.blogRowSummary &&
      blog.blogRowSummary.blogRowSummary &&
      blog.blogRowSummary.blogRowSummary.length > 0
    ) {
      setSummary(blog.blogRowSummary.blogRowSummary)
    } else {
      setSummary(blog.summary.summary)
    }

    setToUrl(`${baseUrl}/${blog.slug}`)
  }, [blog, baseUrl])

  return (
    <div className="col grid-lister__item d-flex flex-column">
      <div className="grid-lister__image">
        <div className="ratio ratio-16x9 homepage-image-height-fix">
          <Link to={toUrl}>
            <GatsbyImage
              className="image__fit--cover override-gatsby homepageImg"
              imgClassName="image__fit--cover"
              image={blogImage}
              alt=""
            ></GatsbyImage>
          </Link>
        </div>
      </div>
      <div className="grid-lister__text d-flex flex-column align-items-start">
        <h4 className={titleClassNames}>
          <Link to={toUrl}>{blog.title}</Link>
        </h4>
        <p className="mt-3">{summary}</p>
      </div>
    </div>
  )
}

export const PageBlockWithoutImage = ({ pageBlock, categoryMicrocopy }) => {
  const [titleClassNames, setTitleClassNames] = useState('force-font__light')

  useEffect(() => {
    setFontWeightClassNames(pageBlock.titleFontWeight, setTitleClassNames)
  }, [pageBlock])

  return (
    <div className="col grid-lister__item d-flex flex-column">
      <div className="grid-lister__image">
        <div className="ratio ratio-16x9 homepage-image-height-fix">
          <a href={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}>
            <img
              src="https://dummyimage.com/300x300/000/fff.png&text=+"
              alt=""
              className="image__fit--cover placeholder-grid-image override-gatsby homepageImg"
            />
          </a>
        </div>
      </div>
      <div className="grid-lister__text d-flex flex-column align-items-start">
        <h4 className={titleClassNames}>
          <Link to={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}>{pageBlock.title}</Link>
        </h4>
        <>
          {pageBlock.description ? (
            <p className="mt-3">{pageBlock.description.description}</p>
          ) : null}
        </>
        <StandardButton
          path={pageBlock.page ? buildUrl(pageBlock.page, '') : '/'}
          icon={StandardButtonArrowIcon}
        >
          {pageBlock.buttonText !== null && pageBlock.buttonText !== ''
            ? pageBlock.buttonText
            : getI18nText(categoryMicrocopy, 'categories.discover')}
        </StandardButton>
      </div>
    </div>
  )
}
